import React from 'react';
import styled, { css } from 'styled-components';
import * as Switch from '@radix-ui/react-switch';
import { fadeIn, hoverTransition } from '../../styles/animations';
import {
    absoluteCenteredVertical,
    flexCenterAll,
    focusState,
    revertOutlineForFocusVisible
} from '../../styles/mixins';
import { motion } from 'framer-motion';
import HBTooltip, { ITooltipProps } from '../HBTooltip/HBTooltip';
import { useMemo } from 'react';
import { MustIncludeOneOfTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { Check12, Check8 } from '@honeybook/icons';
import { TControlSize } from '../../tokens/types';
import { vars } from '../../theme';
import { isAriaInvalid } from '../common/internal/isAriaInvalid';

/* ------------------------------ Constants ------------------------------ */

const CHECKED_ICONS = {
    sm: Check8,
    md: Check8,
    lg: Check12
} as const;

const SIZES = {
    sm: {
        width: '28px',
        height: '16px',
        thumbSize: '12px',
        thumbOffset: '2px',
        checkRatio: '12px'
    },
    md: {
        width: '34px',
        height: '20px',
        thumbSize: '14px',
        thumbOffset: '3px',
        checkRatio: '14px'
    },
    lg: {
        width: '42px',
        height: '24px',
        thumbSize: '18px',
        thumbOffset: '3px',
        checkRatio: '18px'
    }
} as const;

const SWITCH_COLORS = {
    checked: {
        default: vars.colors.text.primary,
        hover: vars.colors.text.secondary,
        disabled: vars.colors.surface.disabled
    },
    unchecked: {
        default: vars.colors.surface.track,
        hover: vars.colors.text.tertiary,
        disabled: vars.colors.surface.disabled
    },
    error: {
        surface: vars.colors.surface.danger,
        border: vars.colors.stroke.danger,
        icon: vars.colors.surface.item
    }
} as const;

const THUMB_COLORS = {
    default: vars.colors.surface.item,
    disabled: vars.colors.surface.item,
    error: vars.colors.stroke.danger
} as const;

/* ------------------------------ Types ------------------------------ */
export type OmittedSwitchProps = Omit<
    Switch.SwitchProps,
    'onChange' | 'disabled'
>;

export type HBSwitchBaseProps = OmittedSwitchProps & {
    id?: string;
    /** HTML `name` attribute forwarded to the underlying Radix Switch input element. */
    name?: string;
    /** Size of the switch: 'sm' (28×16 px, default), 'md' (34×20 px), or 'lg' (42×24 px). */
    size?: TControlSize;
    /** Fires when the switch is toggled, receiving the new checked state as a boolean. Built on Radix Switch; this is Radix's `onCheckedChange` exposed as `onChange`. Use `onChange`, not the Radix name. */
    onChange: Switch.SwitchProps['onCheckedChange'];
    /** Used as the accessible name, announced as "{label} on" / "{label} off". Ignored for naming when `aria-labelledby` is set. */
    label: string;
    /** Accessible name for the switch, overriding the "{label} on/off" default (e.g. an `HBFieldControl` with `ariaLabel`). A valid `aria-labelledby` still wins over it. Omitted: "{label} on/off". */
    'aria-label'?: string;
    /** ID of the element that names the switch (e.g. an `HBFieldControl` label). A valid reference wins over the "{label} on/off" aria-label, which stays as the fallback name when it points at nothing. */
    'aria-labelledby'?: string;
    /** Space-separated IDs of hint/error elements describing the switch. Omitted: no attribute. */
    'aria-describedby'?: string;
    /** Marks the switch invalid for assistive tech without error styling. `aria-invalid="true"` is set when this or `error` is true. Omitted: follows `error` only. */
    'aria-invalid'?: React.AriaAttributes['aria-invalid'];
    'data-testid'?: string;
    /** CSS class applied to the root element. */
    className?: string;
    /** Hides the animated checkmark icon inside the thumb when checked. */
    hideCheckIcon?: boolean;
    /** Tooltip text shown on hover when the switch is enabled; when disabled, `disabledTooltipText` takes precedence. */
    toolTipText?: string;
    /** Additional props forwarded to the HBTooltip wrapper (placement, offset, etc.). */
    toolTipProps?: Partial<Omit<ITooltipProps, 'target'>>;
    /** Applies error styling (red surface and thumb) to the switch. */
    error?: boolean;
};

export type HBSwitchDisabledProps = {
    disabled: boolean;
    /** Required tooltip text shown when the switch is disabled; enforced by TypeScript when `disabled` is provided. */
    disabledTooltipText: string;
    /** Additional props forwarded to the HBTooltip shown when the switch is disabled (placement, offset, etc.). */
    disabledTooltipProps?: Partial<Omit<ITooltipProps, 'target'>>;
};

export type HBSwitchProps = HBSwitchBaseProps & HBSwitchDisabledProps;

type StyledProps = Partial<
    Pick<HBSwitchProps, 'size' | 'checked' | 'disabled' | 'error'>
>;

/* ------------------------------ Component ------------------------------ */
function HBSwitch(
    props: HBSwitchBaseProps & MustIncludeOneOfTestIdProps
): JSX.Element;
function HBSwitch(
    props: HBSwitchProps & MustIncludeOneOfTestIdProps
): JSX.Element;
function HBSwitch({
    id,
    name,
    testIdName,
    testId,
    size = 'sm',
    checked,
    onChange,
    disabled,
    disabledTooltipText,
    disabledTooltipProps,
    label,
    className,
    hideCheckIcon = false,
    toolTipText,
    toolTipProps,
    error = false,
    'aria-label': ariaLabelProp,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid
}: HBSwitchProps & MustIncludeOneOfTestIdProps) {
    const animationVariants = useMemo(() => getAnimationVariants(size), [size]);

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id || name,
        suffix: 'switch-label',
        componentName: 'HBSwitch'
    });

    const isInvalid = isAriaInvalid(error, ariaInvalid);
    const ariaLabel =
        ariaLabelProp ?? (checked ? `${label} on` : `${label} off`);

    const actualToolTipProps = disabled ? disabledTooltipProps : toolTipProps;
    const actualToolTipText = disabled ? disabledTooltipText : toolTipText;

    const switchCmp = (
        <Root
            id={id}
            name={name}
            size={size}
            checked={checked}
            disabled={disabled}
            error={error}
            onCheckedChange={onChange}
            data-testid={finalTestId}
            className={className}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            aria-invalid={isInvalid ? 'true' : undefined}
        >
            <Thumb
                size={size}
                checked={checked}
                initial={false}
                animate={checked ? 'checked' : 'unchecked'}
                variants={animationVariants}
                error={error}
                disabled={disabled}
            />

            {!hideCheckIcon && checked && (
                <CheckIcon
                    size={size}
                    disabled={disabled}
                    checked={checked}
                    error={error}
                />
            )}
        </Root>
    );

    return (
        // When tooltip is disabled it will render the target only. We only show a tooltip here when the switch
        // is disabled.
        <HBTooltip
            target={switchCmp}
            placement="top"
            disabled={!disabled && !toolTipText}
            offsetY={6}
            targetContainerStyle={targetContainerStyle}
            {...actualToolTipProps}
        >
            {actualToolTipText}
        </HBTooltip>
    );
}

export default HBSwitch;

/* ------------------------------ Helpers ------------------------------ */
const targetContainerStyle: React.CSSProperties = { display: 'inline-flex' };

function sizeProp(prop: keyof (typeof SIZES)['sm']) {
    return ({ size }: StyledProps) => SIZES[size][prop];
}

function getBackgroundColor({ checked, disabled, error }: StyledProps) {
    // no hover interaction when disabled
    if (disabled) {
        return css`
            background-color: ${checked
                ? SWITCH_COLORS.checked.disabled
                : SWITCH_COLORS.unchecked.disabled};
        `;
    }

    if (error) {
        return css`
            background-color: ${SWITCH_COLORS.error.surface};
            box-shadow: 0 0 0 1px ${SWITCH_COLORS.error.border} inset;
        `;
    }

    return css`
        ${hoverTransition(
            'background-color',
            checked
                ? SWITCH_COLORS.checked.default
                : SWITCH_COLORS.unchecked.default,
            checked
                ? SWITCH_COLORS.checked.hover
                : SWITCH_COLORS.unchecked.hover,
            undefined,
            undefined,
            false
        )};
    `;
}

function getAnimationVariants(size: TControlSize) {
    // This works, but the more correct thing is to have right: SIZES[size].thumbOffset here
    // using right doesn't animate in this current version of framer-motion
    // it does work in the latest version, but, that has issues with our webpack build in
    // honeybook-react.
    // TL;DR - when framer-motion is updated to v6, change this to: right: SIZES[size].thumbOffset
    return {
        checked: {
            left: `calc(100% - ${
                parseInt(SIZES[size].thumbOffset) +
                parseInt(SIZES[size].thumbSize)
            }px)`
        },
        unchecked: {
            left: `${SIZES[size].thumbOffset}`
        }
    };
}

/* ------------------------------ Root ------------------------------ */
const Root = styled(Switch.Root)<StyledProps>`
    &&& {
        width: fit-content;
        min-width: ${sizeProp('width')};
        height: ${sizeProp('height')};
        border-radius: ${sizeProp('width')};
        border: none;
        position: relative;
        padding: 0;
        cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
        box-sizing: content-box;

        ${getBackgroundColor};
        ${revertOutlineForFocusVisible};
    }

    // Required in order to override angular button:focus styles
    &&&:focus-visible {
        ${getBackgroundColor};
    }

    ${focusState()}

    svg * {
        fill: ${({ checked, error, disabled }) => {
            if (error && !disabled) {
                return SWITCH_COLORS.error.surface;
            }

            if (checked) {
                return disabled
                    ? SWITCH_COLORS.checked.disabled
                    : SWITCH_COLORS.checked.default;
            }

            return disabled
                ? SWITCH_COLORS.unchecked.disabled
                : SWITCH_COLORS.unchecked.default;
        }};
    }

    &:hover {
        svg * {
            fill: ${({ checked, error, disabled }) => {
                if (disabled) {
                    return;
                }

                if (error) {
                    return SWITCH_COLORS.error.surface;
                }

                if (checked) {
                    return SWITCH_COLORS.checked.hover;
                }

                return SWITCH_COLORS.unchecked.hover;
            }};
        }
    }
`;

/* ------------------------------ Thumb ------------------------------ */
const Thumb = styled(
    motion(Switch.Thumb, {
        forwardMotionProps: true
    })
)<StyledProps>`
    ${absoluteCenteredVertical};
    height: ${sizeProp('thumbSize')};
    width: ${sizeProp('thumbSize')};
    border-radius: ${sizeProp('thumbSize')};
    background-color: ${THUMB_COLORS.default};

    ${({ error }) =>
        error &&
        css`
            background-color: ${THUMB_COLORS.error};
        `}

    // Note: we override error color when disabled
    ${({ disabled }) =>
        disabled &&
        css`
            background-color: ${THUMB_COLORS.disabled};
        `}
`;

/* ------------------------------ Check Icon ------------------------------ */
const CheckIcon = styled(({ size, ...props }) => {
    const CheckComponent = CHECKED_ICONS[size];
    return <CheckComponent {...props} />;
})`
    ${flexCenterAll};
    line-height: 0;
    position: absolute;
    opacity: 0;
    animation: ${fadeIn} 0.4s ease-in-out forwards;
    width: ${sizeProp('checkRatio')};
    height: ${sizeProp('checkRatio')};
    top: ${sizeProp('thumbOffset')};
    ${({ checked }) =>
        checked
            ? css`
                  right: ${sizeProp('thumbOffset')};
                  left: auto;
              `
            : css`
                  left: ${sizeProp('thumbOffset')};
                  right: auto;
              `};
`;
